import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import {
  Image,
  ImageBackground,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  FadeInUp,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import {
  iqesColors,
  iqesGlow,
  iqesGradients,
  iqesInteractionStates,
  iqesRadii,
  iqesTypography,
} from './iqesDesignSystem';
import { iqesSceneAssets, iqesScenePresets, type IQESSceneKey } from './iqesSceneSystem';
import useReducedMotion from './useReducedMotion';

type AnswerState = 'default' | 'selected' | 'correct' | 'incorrect' | 'disabled';

export const IQESSceneBackdrop = ({
  sceneKey,
  style,
  children,
}: {
  sceneKey: IQESSceneKey;
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
}) => {
  const preset = iqesScenePresets[sceneKey];
  const ambience = useSharedValue(0);
  const reduceMotion = useReducedMotion();

  React.useEffect(() => {
    ambience.value = reduceMotion
      ? withTiming(0, { duration: 80 })
      : withRepeat(
        withSequence(
          withTiming(1, { duration: 4200, easing: Easing.inOut(Easing.cubic) }),
          withTiming(0, { duration: 4200, easing: Easing.inOut(Easing.cubic) })
        ),
        -1,
        false
      );
  }, [ambience, reduceMotion]);

  const glowStyle = useAnimatedStyle(() => ({
    opacity: 0.16 + ambience.value * 0.12,
    transform: [
      { translateX: -8 + ambience.value * 16 },
      { scale: 1.02 + ambience.value * 0.04 },
    ],
  }));

  const veilStyle = useAnimatedStyle(() => ({
    opacity: 0.22 + ambience.value * 0.1,
    transform: [{ translateY: -6 + ambience.value * 12 }],
  }));

  const particleStyle = useAnimatedStyle(() => ({
    opacity: 0.32 + ambience.value * 0.24,
    transform: [{ translateY: 5 - ambience.value * 10 }],
  }));

  return (
    <ImageBackground
      source={preset.image}
      resizeMode="cover"
      style={[styles.sceneBackdrop, style]}
      imageStyle={styles.sceneBackdropImage}
    >
      <LinearGradient colors={preset.overlay} style={StyleSheet.absoluteFill} />
      <Animated.View style={[styles.sceneWarmGlow, glowStyle]} pointerEvents="none" />
      <Animated.View style={[styles.sceneSoftVeil, veilStyle]} pointerEvents="none" />
      <Animated.View style={[styles.sceneParticle, styles.sceneParticleOne, particleStyle]} pointerEvents="none" />
      <Animated.View style={[styles.sceneParticle, styles.sceneParticleTwo, particleStyle]} pointerEvents="none" />
      {children}
    </ImageBackground>
  );
};

export const IQESParchmentCard = ({
  prompt,
  reference,
  compact = false,
  style,
  children,
}: {
  prompt: string;
  reference?: string;
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
}) => {
  const parchmentBreath = useSharedValue(0);
  const reduceMotion = useReducedMotion();

  React.useEffect(() => {
    parchmentBreath.value = reduceMotion
      ? withTiming(0, { duration: 80 })
      : withRepeat(
        withSequence(
          withTiming(1, { duration: 3400, easing: Easing.inOut(Easing.cubic) }),
          withTiming(0, { duration: 3400, easing: Easing.inOut(Easing.cubic) })
        ),
        -1,
        false
      );
  }, [parchmentBreath, reduceMotion]);

  const parchmentGlowStyle = useAnimatedStyle(() => ({
    opacity: 0.18 + parchmentBreath.value * 0.16,
    transform: [{ scale: 0.96 + parchmentBreath.value * 0.08 }],
  }));

  const edgeLightStyle = useAnimatedStyle(() => ({
    opacity: 0.26 + parchmentBreath.value * 0.18,
  }));

  return (
    <Animated.View
      entering={FadeInUp.duration(360).easing(Easing.out(Easing.cubic))}
      style={styles.parchmentShell}
    >
      <Animated.View style={[styles.parchmentOuterGlow, parchmentGlowStyle]} pointerEvents="none" />
      <View style={[styles.parchment, compact && styles.parchmentCompact, style, styles.parchmentSurface]}>
        <Image
          source={iqesSceneAssets.parchment}
          resizeMode="stretch"
          style={styles.parchmentImage}
          pointerEvents="none"
        />
        <LinearGradient colors={iqesGradients.parchmentWarmth} style={StyleSheet.absoluteFill} />
        <Animated.View style={[styles.parchmentEdgeLight, edgeLightStyle]} pointerEvents="none" />
        {children || <Text style={[styles.parchmentText, compact && styles.parchmentTextCompact]}>{prompt}</Text>}
        {!!reference && <Text style={styles.parchmentReference}>{reference}</Text>}
      </View>
    </Animated.View>
  );
};

export const IQESAnswerOption = ({
  label,
  index,
  state = 'default',
  onPress,
  disabled = false,
  trailingIcon,
  style,
  testID,
  onPressIn,
  onPressOut,
  accessibilityLabel,
  badgeLabel,
  hideBadge = false,
  prominent = false,
  showDefaultIcon = false,
}: {
  label: string;
  index?: number;
  state?: AnswerState;
  onPress?: () => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
  disabled?: boolean;
  trailingIcon?: keyof typeof Ionicons.glyphMap;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  accessibilityLabel?: string;
  badgeLabel?: string;
  hideBadge?: boolean;
  prominent?: boolean;
  showDefaultIcon?: boolean;
}) => {
  const stateStyle = {
    default: iqesInteractionStates.resting,
    selected: iqesInteractionStates.selected,
    correct: iqesInteractionStates.correct,
    incorrect: iqesInteractionStates.incorrect,
    disabled: {
      borderColor: 'rgba(255,255,255,0.12)',
      backgroundColor: 'rgba(255,255,255,0.04)',
    },
  }[state];
  const isPressedDisabled = disabled || state === 'disabled';
  const letter = badgeLabel || (typeof index === 'number' ? String.fromCharCode(65 + index) : null);
  const isEmotionState = state === 'selected' || state === 'correct' || state === 'incorrect';
  const glowProgress = useSharedValue(isEmotionState ? 1 : 0);
  const selectionPulse = useSharedValue(0);
  const reduceMotion = useReducedMotion();

  React.useEffect(() => {
    glowProgress.value = withTiming(isEmotionState ? 1 : 0, {
      duration: reduceMotion ? 80 : 260,
      easing: Easing.out(Easing.cubic),
    });

    selectionPulse.value = isEmotionState
      ? reduceMotion
        ? withTiming(0, { duration: 80 })
        : withRepeat(
        withSequence(
          withTiming(1, { duration: 900, easing: Easing.inOut(Easing.cubic) }),
          withTiming(0, { duration: 900, easing: Easing.inOut(Easing.cubic) })
        ),
        -1,
        false
      )
      : withTiming(0, { duration: 180 });
  }, [glowProgress, isEmotionState, reduceMotion, selectionPulse]);

  const glowAnimatedStyle = useAnimatedStyle(() => ({
    opacity: glowProgress.value * (0.18 + selectionPulse.value * 0.16),
    transform: [{ scale: 1 + glowProgress.value * (0.018 + selectionPulse.value * 0.012) }],
  }));

  const sheenAnimatedStyle = useAnimatedStyle(() => ({
    opacity: glowProgress.value * (0.22 + selectionPulse.value * 0.18),
    transform: [
      { translateX: -24 + selectionPulse.value * 48 },
      { rotate: '-10deg' },
    ],
  }));

  const iconAnimatedStyle = useAnimatedStyle(() => {
    const visibleAtRest = showDefaultIcon && !isEmotionState;
    return {
      opacity: visibleAtRest ? 1 : glowProgress.value,
      transform: [{ scale: visibleAtRest ? 1 : 0.82 + glowProgress.value * (0.18 + selectionPulse.value * 0.05) }],
    };
  });
  const entranceDelay = typeof index === 'number' ? Math.min(index * 55, 220) : 0;
  const activeGlowStyle = state === 'incorrect'
    ? styles.answerEmotionIncorrect
    : state === 'correct'
      ? styles.answerEmotionCorrect
      : styles.answerEmotionSelected;
  const statusIcon = state === 'incorrect'
    ? 'close'
    : state === 'correct'
      ? 'checkmark'
      : state === 'selected'
        ? 'sparkles'
        : trailingIcon;
  const statusColor = state === 'incorrect'
    ? '#FCA5A5'
    : state === 'correct'
      ? '#BBF7D0'
      : iqesColors.goldSoft;

  return (
    <Animated.View
      entering={reduceMotion ? undefined : FadeInUp.delay(entranceDelay).duration(260).easing(Easing.out(Easing.cubic))}
      style={styles.answerShell}
    >
      {isEmotionState && (
        <>
          <Animated.View style={[styles.answerEmotionGlow, activeGlowStyle, glowAnimatedStyle]} pointerEvents="none" />
          <Animated.View style={[styles.answerSheen, sheenAnimatedStyle]} pointerEvents="none" />
        </>
      )}
      <Pressable
        testID={testID}
        style={({ pressed }) => [
          styles.answerOption,
          prominent && styles.answerOptionProminent,
          stateStyle,
          state === 'selected' && iqesGlow.gold,
          isEmotionState && styles.answerOptionActive,
          isPressedDisabled && styles.answerDisabled,
          pressed && !isPressedDisabled && styles.answerPressed,
          style,
        ]}
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={isPressedDisabled}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ selected: state === 'selected', disabled: isPressedDisabled }}
      >
        {!!letter && !hideBadge && (
          <View style={[styles.answerLetter, prominent && styles.answerLetterProminent, isEmotionState && styles.answerLetterActive]}>
            <Text style={styles.answerLetterText}>{letter}</Text>
          </View>
        )}
        {!!statusIcon && prominent && (
          <Animated.View style={[styles.answerStatusOrb, prominent && styles.answerStatusOrbProminent, iconAnimatedStyle]}>
            <Ionicons name={statusIcon} size={prominent ? 34 : 16} color={statusColor} />
          </Animated.View>
        )}
        <Text style={[
          styles.answerText,
          prominent && styles.answerTextProminent,
          state !== 'default' && styles.answerTextEmphasis,
        ]}>{label}</Text>
        {!!statusIcon && !prominent && (
          <Animated.View style={[styles.answerStatusOrb, iconAnimatedStyle]}>
            <Ionicons name={statusIcon} size={16} color={statusColor} />
          </Animated.View>
        )}
      </Pressable>
    </Animated.View>
  );
};

export const IQESFeedbackCard = ({
  title,
  body,
  xp,
  tone = 'correct',
  style,
}: {
  title: string;
  body: string;
  xp?: number;
  tone?: 'correct' | 'incorrect';
  style?: StyleProp<ViewStyle>;
}) => {
  const isWrong = tone === 'incorrect';
  const feedbackPulse = useSharedValue(0);
  const reduceMotion = useReducedMotion();

  React.useEffect(() => {
    feedbackPulse.value = reduceMotion
      ? withTiming(0, { duration: 80 })
      : withRepeat(
        withSequence(
          withTiming(1, { duration: 1100, easing: Easing.out(Easing.cubic) }),
          withTiming(0, { duration: 1100, easing: Easing.inOut(Easing.cubic) })
        ),
        -1,
        false
      );
  }, [feedbackPulse, reduceMotion]);

  const iconPulseStyle = useAnimatedStyle(() => ({
    opacity: 0.82 + feedbackPulse.value * 0.18,
    transform: [{ scale: 0.94 + feedbackPulse.value * 0.1 }],
  }));

  const feedbackGlowStyle = useAnimatedStyle(() => ({
    opacity: 0.12 + feedbackPulse.value * 0.16,
    transform: [{ scale: 0.98 + feedbackPulse.value * 0.06 }],
  }));

  return (
    <LinearGradient
      colors={isWrong ? iqesGradients.feedbackIncorrect : iqesGradients.feedbackCorrect}
      style={[styles.feedbackCard, isWrong && styles.feedbackWrong, style]}
    >
      <Animated.View style={[styles.feedbackAmbientGlow, feedbackGlowStyle]} pointerEvents="none" />
      <Animated.View style={[styles.feedbackIconWrap, iconPulseStyle]}>
        <Ionicons name={isWrong ? 'sad' : 'sparkles'} size={32} color={isWrong ? '#F87171' : iqesColors.gold} />
      </Animated.View>
      <Text style={[styles.feedbackTitle, isWrong && styles.feedbackWrongTitle]}>{title}</Text>
      <Text style={styles.feedbackBody}>{body}</Text>
      {!!xp && (
        <Text style={styles.xpLine}>
          <Text style={styles.xpBadge}>XP</Text>  +{xp}
        </Text>
      )}
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  sceneBackdrop: {
    overflow: 'hidden',
    backgroundColor: iqesColors.canvasRaised,
  },
  sceneBackdropImage: {
    borderRadius: iqesRadii.panel,
  },
  sceneWarmGlow: {
    position: 'absolute',
    width: '74%',
    height: '92%',
    right: -30,
    top: -24,
    borderRadius: 999,
    backgroundColor: 'rgba(251, 191, 36, 0.32)',
  },
  sceneSoftVeil: {
    position: 'absolute',
    left: -20,
    right: -20,
    bottom: -18,
    height: '42%',
    borderRadius: 999,
    backgroundColor: 'rgba(8, 11, 30, 0.48)',
  },
  sceneParticle: {
    position: 'absolute',
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(253, 230, 138, 0.9)',
  },
  sceneParticleOne: {
    top: '22%',
    left: '18%',
  },
  sceneParticleTwo: {
    top: '34%',
    right: '20%',
    width: 3,
    height: 3,
  },
  parchmentShell: {
    width: '100%',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  parchmentOuterGlow: {
    position: 'absolute',
    top: -18,
    bottom: -18,
    left: -18,
    right: -18,
    borderRadius: 18,
    backgroundColor: 'rgba(251, 191, 36, 0.34)',
  },
  parchment: {
    width: '100%',
    minHeight: 138,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
    paddingVertical: 16,
    overflow: 'hidden',
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#A56424',
    backgroundColor: '#D7A65C',
    ...iqesGlow.parchment,
  },
  parchmentSurface: {
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: '#A56424',
    backgroundColor: '#D7A65C',
  },
  parchmentCompact: {
    minHeight: 168,
  },
  parchmentImage: {
    ...StyleSheet.absoluteFillObject,
    width: undefined,
    height: undefined,
    borderRadius: 8,
    opacity: 0.98,
  },
  parchmentEdgeLight: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(253, 230, 138, 0.55)',
  },
  parchmentText: {
    color: iqesColors.parchmentInk,
    ...iqesTypography.verse,
    fontFamily: iqesTypography.displayFamily,
    textAlign: 'center',
    zIndex: 2,
  },
  parchmentTextCompact: {
    fontSize: 18,
    lineHeight: 27,
  },
  parchmentReference: {
    color: '#1B1007',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 10,
    zIndex: 2,
  },
  answerOption: {
    minHeight: 50,
    borderRadius: iqesRadii.answer,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    overflow: 'hidden',
  },
  answerOptionProminent: {
    minHeight: 94,
    paddingHorizontal: 16,
    paddingVertical: 18,
    borderWidth: 1.5,
  },
  answerShell: {
    position: 'relative',
    overflow: 'visible',
  },
  answerOptionActive: {
    minHeight: 54,
  },
  answerPressed: {
    transform: [{ scale: 0.985 }],
  },
  answerEmotionGlow: {
    position: 'absolute',
    top: -3,
    bottom: -3,
    left: -3,
    right: -3,
    borderRadius: iqesRadii.answer + 5,
    borderWidth: 1,
  },
  answerEmotionSelected: {
    backgroundColor: 'rgba(250, 204, 21, 0.24)',
    borderColor: 'rgba(253, 230, 138, 0.8)',
  },
  answerEmotionCorrect: {
    backgroundColor: 'rgba(34, 197, 94, 0.24)',
    borderColor: 'rgba(187, 247, 208, 0.8)',
  },
  answerEmotionIncorrect: {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderColor: 'rgba(252, 165, 165, 0.76)',
  },
  answerSheen: {
    position: 'absolute',
    top: 2,
    bottom: 2,
    width: 38,
    borderRadius: iqesRadii.answer,
    backgroundColor: 'rgba(255, 248, 232, 0.16)',
    zIndex: 1,
  },
  answerDisabled: {
    opacity: 0.72,
  },
  answerLetter: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#D4A33A',
    marginRight: 10,
  },
  answerLetterProminent: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
    borderColor: iqesColors.goldSoft,
    backgroundColor: 'rgba(251, 191, 36, 0.12)',
  },
  answerLetterActive: {
    backgroundColor: 'rgba(253, 230, 138, 0.12)',
    borderColor: iqesColors.goldSoft,
  },
  answerLetterText: {
    color: iqesColors.goldSoft,
    fontSize: 13,
    fontWeight: '800',
  },
  answerText: {
    color: '#FFFFFF',
    fontSize: 15,
    lineHeight: 21,
    flex: 1,
  },
  answerTextProminent: {
    fontSize: 21,
    lineHeight: 27,
    fontWeight: '900',
    letterSpacing: 0.2,
    flex: 0,
    textAlign: 'center',
  },
  answerTextEmphasis: {
    fontWeight: '700',
  },
  answerStatusOrb: {
    width: 28,
    height: 28,
    borderRadius: 14,
    marginLeft: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(3, 5, 16, 0.46)',
    borderWidth: 1,
    borderColor: 'rgba(253, 230, 138, 0.24)',
  },
  answerStatusOrbProminent: {
    width: 56,
    height: 56,
    borderRadius: 28,
    marginLeft: 0,
    marginTop: 12,
    backgroundColor: 'rgba(3, 5, 16, 0.36)',
    borderColor: 'rgba(253, 230, 138, 0.4)',
  },
  feedbackCard: {
    minHeight: 160,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(245,158,11,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    overflow: 'hidden',
  },
  feedbackAmbientGlow: {
    position: 'absolute',
    width: '82%',
    height: '92%',
    borderRadius: 999,
    backgroundColor: 'rgba(251, 191, 36, 0.38)',
  },
  feedbackWrong: {
    borderColor: 'rgba(239,68,68,0.5)',
  },
  feedbackIconWrap: {
    position: 'absolute',
    left: 18,
    top: 22,
  },
  feedbackTitle: {
    color: '#9DF871',
    fontSize: 22,
    fontWeight: '900',
    fontFamily: iqesTypography.displayFamily,
    textAlign: 'center',
  },
  feedbackWrongTitle: {
    color: '#FCA5A5',
  },
  feedbackBody: {
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginTop: 8,
  },
  xpLine: {
    color: iqesColors.gold,
    fontSize: 24,
    fontWeight: '900',
    marginTop: 12,
  },
  xpBadge: {
    color: '#FFFFFF',
    fontSize: 14,
  },
});
